import { getApiBaseUrl, Hair_API_PREFIX } from './apiConfig';
import { fetchWithTimeout } from './fetchWithTimeout';

function detectionsFromResults(hairResults) {
  const fromPos = [];
  const pr = hairResults?.position_results || hairResults?.positionResults || {};
  Object.values(pr).forEach((pos) => {
    (pos?.detections || []).forEach((d) => fromPos.push(d));
    (pos?.raw_detections || []).forEach((d) => fromPos.push(d));
  });
  if (fromPos.length) return fromPos;
  return (
    hairResults?.results?.all_detections ||
    hairResults?.detections ||
    []
  );
}

function formatMetric(value, digits = 2) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toFixed(digits);
}

function depthLevel(value) {
  const v = Number(value) || 0;
  if (v <= 0.35) return 'Shallow';
  if (v <= 0.55) return 'Moderate';
  return 'Deep';
}

function hairDensityLevel(value) {
  const v = Number(value) || 0;
  if (v <= 0.12) return 'Sparse';
  if (v <= 0.25) return 'Moderate';
  if (v <= 0.4) return 'Dense';
  return 'Very Dense';
}

function scalpExposureLevel(value) {
  const v = Number(value) || 0;
  if (v <= 0.45) return 'Low';
  if (v <= 0.65) return 'Moderate';
  return 'High';
}

function thicknessLevel(value) {
  const v = Number(value) || 0;
  if (v < 2.5) return 'Very Fine';
  if (v < 4) return 'Fine';
  if (v < 6) return 'Moderate';
  if (v < 8.5) return 'Thick';
  return 'Very Thick';
}

function gapLevel(value) {
  const v = Number(value) || 0;
  if (v <= 20) return 'Narrow';
  if (v <= 45) return 'Moderate';
  return 'Wide';
}

function follicleSpacingLevel(value) {
  const v = Number(value) || 0;
  if (v <= 25) return 'Close';
  if (v <= 45) return 'Moderate';
  return 'Sparse';
}

function levelMeaning(level) {
  const map = {
    Sparse: 'Coverage appears lower than typical for this view.',
    'Very Dense': 'Strong coverage across the analyzed region.',
    Dense: 'Healthy coverage density for the analyzed region.',
    Moderate: 'Within a typical clinical range.',
    Low: 'Favorable — limited visible scalp exposure.',
    High: 'Elevated exposure — monitor density and parting width.',
    Narrow: 'Gaps between strands are tightly packed.',
    Wide: 'Wider gaps may indicate reduced local density.',
    Fine: 'Strand caliber is on the finer side.',
    'Very Fine': 'Very fine strands — prioritize strengthening care.',
    Thick: 'Robust strand caliber observed.',
    'Very Thick': 'Very thick strand caliber observed.',
    Close: 'Follicle points are closely spaced.',
    Shallow: 'Surface depth variation is relatively shallow.',
    Deep: 'Greater surface depth variation in this region.',
  };
  return map[level] || 'This measurement falls in a typical range.';
}

/**
 * Build the same analyzer dashboard fields shown on Hair Analysis & Routine.
 */
export function buildAnalyzerPayload(hairResults) {
  const detections = detectionsFromResults(hairResults);
  const labels = detections.map((c) => String(c.label || '').toLowerCase());

  // Concerns (grouped by label, max confidence) — mirrors HairDiagnosticSection
  const groups = {};
  detections.forEach((d) => {
    const name = d.label || d.pure_label || 'Unknown';
    const nameLower = String(name).toLowerCase();
    if (nameLower === 'healthy scalp' || nameLower === 'normal scalp') return;
    const conf = parseFloat(d.confidence) || 0;
    if (!groups[name]) groups[name] = { label: name, value: 0 };
    groups[name].value = Math.max(groups[name].value, conf);
  });
  const concerns = Object.values(groups)
    .map((g) => {
      const val = Math.min(100, g.value);
      return {
        label: g.label,
        value: Number(val.toFixed(1)),
        status: val < 3 ? 'LOW' : val >= 10 ? 'SEVERE' : val >= 5 ? 'MODERATE' : 'FAIR',
      };
    })
    .sort((a, b) => b.value - a.value);

  const overview = [
    { label: 'Shine', status: 'Normal' },
    { label: 'Volume', status: 'Medium' },
    { label: 'Thickness', status: 'Medium' },
    { label: 'Texture', status: hairResults?.hair_type || 'Wavy' },
    { label: 'Cuticle Alignment', status: 'Good Alignment' },
    { label: 'Scalp Hydration', status: 'Optimal' },
  ];

  const attention = [];
  const professional_insights = [];

  if (labels.some((l) => l.includes('thinning') || l.includes('baldness'))) {
    attention.push({ label: 'Hair Density', status: 'Moderate' });
    professional_insights.push('Lower density detected at crown area compared to sides.');
    overview[1].status = 'Low';
  } else {
    attention.push({ label: 'Hair Density', status: 'Healthy' });
  }

  if (labels.some((l) => l.includes('dandruff') || l.includes('scaling') || l.includes('greasy'))) {
    attention.push({ label: 'Scalp Condition', status: labels.some((l) => l.includes('dandruff')) ? 'Mild Dandruff' : 'Monitor' });
    professional_insights.push('Mild dandruff or oil imbalance detected on scalp. Recommend clarifying treatment.');
    overview[5].status = 'Monitor';
  } else {
    attention.push({ label: 'Scalp Condition', status: 'Clean' });
  }

  if (labels.some((l) => l.includes('recession') || l.includes('hairline'))) {
    attention.push({ label: 'Hairline Status', status: 'Slight Recession' });
    professional_insights.push('Slight recession observed at hairline.');
  }

  if (labels.some((l) => l.includes('rough') || l.includes('straw') || l.includes('frizz'))) {
    attention.push({ label: 'Frizz Level', status: 'Medium' });
    professional_insights.push('Frizz is more visible near hairline and sides.');
    overview[0].status = 'Dry';
    overview[4].status = 'Fair';
  }

  if (labels.some((l) => l.includes('bug') || l.includes('insect'))) {
    attention.push({ label: 'Scalp Parasites', status: 'Active Bugs' });
    professional_insights.push('Hair bugs detected. Immediate specialized anti-parasitic treatment required.');
  }

  if (labels.some((l) => l.includes('rash') || l.includes('pimple'))) {
    attention.push({ label: 'Inflammation', status: 'Irritated' });
    professional_insights.push('Scalp inflammation or pimples observed. Recommend soothing treatment.');
  }

  if (professional_insights.length === 0) {
    professional_insights.push('Scalp appears healthy and well-hydrated.');
    professional_insights.push('Hair cuticle shows good alignment and shine.');
  }

  const etiology = [];
  const attentionLabels = attention.map((a) => a.label.toLowerCase());
  if (attentionLabels.some((l) => l.includes('dandruff') || l.includes('scalp'))) {
    etiology.push('Potential overactive sebaceous glands or microbial (Malassezia) imbalance on the scalp surface.');
  }
  if (attentionLabels.some((l) => l.includes('density') || l.includes('hairline'))) {
    etiology.push('Follicle miniaturization patterns often linked to hormonal shifts, prolonged stress, or localized nutrient deficiencies.');
  }
  if (attentionLabels.some((l) => l.includes('frizz') || l.includes('texture'))) {
    etiology.push('Elevated hair porosity and cuticle lifting due to environmental humidity or lack of protective lipid layers.');
  }
  if (etiology.length === 0) {
    etiology.push('High-fidelity synthesis confirms a balanced hair cycle and optimal nutrient delivery to the follicles.');
  }

  const health_indices = {
    Density: labels.some((l) => l.includes('thinning') || l.includes('baldness')) ? 62 : 94,
    Shine: labels.some((l) => l.includes('rough') || l.includes('straw')) ? 58 : 92,
    Frizz: labels.some((l) => l.includes('frizz') || l.includes('texture')) ? 64 : 88,
    Volume: labels.some((l) => l.includes('thinning')) ? 60 : 90,
    'Oil Balance': labels.some((l) => l.includes('oil') || l.includes('greasy')) ? 68 : 95,
  };

  // Confidence — mirrors HairDiagnosticSection
  const baseConf = hairResults?.confidence ?? 72;
  let confValue = Math.min(99, baseConf);
  let confReason = 'AI scan completed with high precision. No significant concerns detected.';
  if (concerns.length) {
    const avgConf = concerns.reduce((acc, c) => acc + c.value, 0) / concerns.length;
    confValue = Math.min(99, Math.max(45, baseConf + (avgConf > 50 ? 5 : -5)));
    confReason = confValue > 88
      ? 'High-fidelity scan: AI has successfully mapped all visible hair and scalp patterns.'
      : `AI Scan: Analyzing ${concerns.length} localized concerns with high-sensitivity surface mapping.`;
  }
  let confLevel = 'High';
  if (confValue < 60) confLevel = 'Low';
  else if (confValue < 85) confLevel = 'Medium';

  // Per-view analysis
  const positionResults = hairResults?.position_results || hairResults?.positionResults || {};
  const view_results = Object.entries(positionResults).map(([view, pos]) => {
    const dets = (pos?.detections || pos?.raw_detections || [])
      .filter((d) => {
        const n = String(d.label || '').toLowerCase();
        return n && n !== 'normal scalp' && n !== 'healthy scalp';
      })
      .map((d) => ({
        label: d.label,
        confidence: Number(parseFloat(d.confidence) || 0).toFixed(1),
      }));
    return {
      view: String(view).charAt(0).toUpperCase() + String(view).slice(1),
      score: pos?.hair_score ?? pos?.score ?? null,
      quality_note: pos?.quality_note || '',
      detections: dets,
    };
  });

  const hair_conditions = hairResults?.hair_conditions || hairResults?.hairConditions || {};

  const hasConcern = concerns.length > 0;
  const diagnostic_summary = hasConcern
    ? 'Your hair shows measurable resilience with localized areas requiring maintenance. Scalp and density markers warrant targeted care while overall structure remains stable.'
    : 'Your hair demonstrates high resilience with stable density. The scalp health is currently optimal, requiring active protection against severe ambient stressors (Extreme UV).';

  const insights = etiology.map((text, i) => ({
    title: i === 0 ? 'Root Cause Synthesis' : `Clinical Factor ${i + 1}`,
    text,
  }));
  if (!hasConcern) {
    insights.unshift({
      title: 'Cuticle Architecture',
      text: 'Cuticle layer displays healthy structural alignment and natural light reflection.',
    });
  }

  return {
    concerns,
    concerns_empty_message: hasConcern
      ? null
      : 'Excellent Hair Health — No significant hair or scalp concerns were detected in this analysis.',
    professional_insights,
    attention_areas: attention,
    health_indices,
    view_results,
    hair_conditions,
    confidence: { level: confLevel, value: Math.round(confValue), reason: confReason },
    condition_overview: overview,
    diagnostic_badges: [
      labels.some((l) => l.includes('dandruff') || l.includes('rash')) ? 'SCALP: ATTENTION' : 'SCALP: CLEAN',
      hasConcern ? 'OVERALL: MONITOR' : 'OVERALL: HEALTHY',
    ],
    diagnostic_summary,
    critical_areas: attention.map((a) => a.label),
    insights,
  };
}

/**
 * Full magnified scalp / hair depthness payload for PDF.
 */
export function buildDepthnessPayload(magnifiedAnalysis) {
  const metrics = magnifiedAnalysis?.metrics;
  if (!metrics) {
    return {
      available: false,
      summary: 'Magnified scalp depth analysis was not available for this scan (no magnified photo or depth pass skipped).',
      metrics: [],
      insights: [],
    };
  }

  const hd = metrics.hair_density || {};
  const exposure = metrics.scalp_exposure_ratio || {};
  const gap = metrics.hair_gap || {};
  const thick = metrics.hair_thickness || {};
  const fol = metrics.follicle_spacing || {};
  const depth = metrics.depth || {};

  const rows = [
    {
      label: 'Hair Density',
      value: formatMetric(hd.value_px, 4),
      unit: 'ratio',
      status: hairDensityLevel(hd.value_px),
    },
    {
      label: 'Scalp Exposure',
      value: exposure.value != null ? `${formatMetric(Number(exposure.value) * 100, 1)}%` : '—',
      unit: 'of image',
      status: scalpExposureLevel(exposure.value),
    },
    {
      label: 'Avg Hair Gap',
      value: formatMetric(gap.average_px, 1),
      unit: 'px',
      status: gapLevel(gap.average_px),
    },
    {
      label: 'Max Hair Gap',
      value: formatMetric(gap.maximum_px, 1),
      unit: 'px',
      status: gapLevel(gap.maximum_px),
    },
    {
      label: 'Gap Count',
      value: String(gap.gap_count ?? '—'),
      unit: 'count',
      status: 'Info',
    },
    {
      label: 'Hair Thickness (mean)',
      value: formatMetric(thick.mean_px, 2),
      unit: 'px',
      status: thick.level || thicknessLevel(thick.mean_px),
    },
    {
      label: 'Hair Thickness (median)',
      value: formatMetric(thick.median_px, 2),
      unit: 'px',
      status: thick.level || thicknessLevel(thick.median_px),
    },
    {
      label: 'Follicle Spacing',
      value: formatMetric(fol.mean_px, 1),
      unit: 'px',
      status: follicleSpacingLevel(fol.mean_px),
    },
    {
      label: 'Follicle Points',
      value: String(fol.follicle_point_count ?? '—'),
      unit: 'count',
      status: 'Info',
    },
  ];

  if (depth.available) {
    rows.push({
      label: 'Relative Depth (hair)',
      value: formatMetric(depth.relative_mean_hair, 4),
      unit: '0–1',
      status: depthLevel(depth.relative_mean_hair),
    });
    rows.push({
      label: 'Relative Depth (scalp)',
      value: formatMetric(depth.relative_mean_scalp, 4),
      unit: '0–1',
      status: depthLevel(depth.relative_mean_scalp),
    });
  }

  const insights = rows
    .filter((r) => r.status && r.status !== 'Info')
    .map((r) => ({
      title: r.label,
      level: r.status,
      text: `${r.value} ${r.unit} — ${levelMeaning(r.status)}`,
    }));

  const secs = magnifiedAnalysis.processing_time_ms
    ? Math.round(Number(magnifiedAnalysis.processing_time_ms) / 1000)
    : null;

  return {
    available: true,
    analysis_id: magnifiedAnalysis.analysis_id || null,
    processing_seconds: secs,
    image: magnifiedAnalysis.image || null,
    summary: secs != null
      ? `Magnified scalp optical analysis completed in ${secs}s — density, spacing, thickness, and relative depth.`
      : 'Magnified scalp optical analysis with density, spacing, thickness, and relative depth metrics.',
    metrics: rows,
    insights,
  };
}

/**
 * Build payload for POST /api/hair/clinical-report-pdf
 */
export function buildClinicalReportPayload({
  hairResults,
  aqiData,
  routineData,
  sessionId,
}) {
  const analyzer = buildAnalyzerPayload(hairResults);
  const depthness = buildDepthnessPayload(
    hairResults?.magnified_analysis || hairResults?.results?.magnified_analysis
  );
  const now = new Date();
  const generated = now.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).replace(',', ' |');

  const location = aqiData?.location || '—';
  const city = String(location).split(',')[0].trim();
  const conf = analyzer.confidence;

  return {
    session_id: sessionId || hairResults?.session_id || '—',
    generated,
    scan_precision: `${conf.level} (${conf.value}%)`,
    confidence: conf,
    overall_score: hairResults?.hair_score ?? hairResults?.hairScore ?? 0,
    summary: hairResults?.summary || analyzer.diagnostic_summary,
    clinical_focus: 'Maintain Scalp Balance & Follicle Strength',
    condition_overview: analyzer.condition_overview,
    diagnostic_badges: analyzer.diagnostic_badges,
    diagnostic_summary: analyzer.diagnostic_summary,
    concerns: analyzer.concerns,
    concerns_empty_message: analyzer.concerns_empty_message,
    professional_insights: analyzer.professional_insights,
    attention_areas: analyzer.attention_areas,
    health_indices: analyzer.health_indices,
    view_results: analyzer.view_results,
    hair_conditions: analyzer.hair_conditions,
    insights_intro: 'Multi-Angle Viewpoint Synthesis (Crown, Left):',
    insights: analyzer.insights,
    depthness,
    morning_routine:
      routineData?.morning_routine || [
        'Cleanse scalp with a pH-balanced shampoo',
        'Apply lightweight protective barrier spray',
        'Gently detangle with a wide-tooth comb',
      ],
    evening_routine:
      routineData?.evening_routine || [
        'Perform scalp massage with revitalizing oil',
        'Apply leave-in moisture hydration cream',
        'Wear a smooth silk wrap during sleep',
      ],
    morning_benefit:
      'Removes residual sebum, shields follicles against Extreme UV degradation, and limits mechanical stress.',
    evening_benefit:
      'Enhances micro-circulation, locks in moisture, and eliminates friction-induced breakage.',
    lifestyle_left: [
      {
        title: 'Biotin & Omega-3',
        text: 'Incorporate eggs, nuts, and healthy fats to strengthen hair follicle cell membranes.',
      },
      {
        title: 'Hydration Benchmark',
        text: 'Target 2.5L daily. Dehydration impairs scalp elasticity and increases shaft brittleness.',
      },
    ],
    lifestyle_right: [
      {
        title: 'Cortisol Regulation',
        text: 'Allocate 10 minutes daily to mindfulness/breathing to protect the regular follicle growth cycle.',
      },
      {
        title: 'UV Protection',
        text: `Utilize headwear during peak sunshine in ${city} to mitigate environmental oxidation.`,
      },
    ],
    disclaimer:
      'This document is an AI-assisted diagnostic evaluation based on multi-view hair analysis, magnified scalp depth metrics, and regional environmental metrics. It does not replace formal clinical consultation. For persistent concerns or systemic hair loss, please consult a certified dermatologist or trichologist.',
  };
}

export async function downloadClinicalReportPdf(payload) {
  const base = (getApiBaseUrl() || '').replace(/\/$/, '');
  const url = `${base}${Hair_API_PREFIX}/clinical-report-pdf`;
  const response = await fetchWithTimeout(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    timeoutMs: 60000,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to generate clinical PDF report');
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = `Hair_Analysis_Report_${new Date().toISOString().slice(0, 10)}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}
